import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, statusBadgeClass, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { Tag, History, RotateCcw, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const TYPE_NAMES = ["Shop","One Room Self-Contain","Room & Parlour Self-Contain","Hall","Two-Bedroom Flat"];

export default function Pricing() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("active");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [a, h] = await Promise.all([api.get("/pricing"), api.get("/pricing/history").catch(()=>({data:[]}))]);
    setActive(a.data); setHistory(h.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const rent = Number(f.get("rent"));
    const caution = Number(f.get("caution"));
    const rate = Number(f.get("commission_rate"));
    const commission = Math.round((rent + caution) * rate / 100);
    try {
      await api.post("/pricing", {
        unit_type_name: f.get("unit_type_name"),
        rent_amount: rent,
        caution_fee: caution,
        agreement_fee: Number(f.get("agreement")),
        commission_rate: rate,
        commission_amount: commission,
        service_charge: Number(f.get("service")),
        discount_amount: Number(f.get("discount") || 0),
        promotion_note: f.get("promo") || "",
        change_reason: f.get("reason") || "",
      });
      toast.success("New pricing version created. Existing leases unchanged.");
      setEditing(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const rollback = async (id) => {
    if (!confirm("Roll back to this pricing version?")) return;
    try {
      await api.post(`/pricing/${id}/rollback`);
      toast.success("Rolled back successfully");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const canEdit = ["admin","manager","lawyer"].includes(user.role);
  const canRollback = user.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Pricing Management</h1>
          <p className="text-[#5E7166] text-sm">Versioned pricing — only new leases/renewals use updated prices.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("active")} className={tab==="active" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"} data-testid="pricing-tab-active">Active</button>
          <button onClick={() => setTab("history")} className={tab==="history" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"} data-testid="pricing-tab-history"><History size={14}/> History</button>
        </div>
      </div>

      {tab === "active" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {active.map(p => (
            <Card key={p.id} testid={`pricing-card-${p.unit_type_name.replace(/\s/g,'-')}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">v{p.version}</div>
                  <h3 className="font-serif text-xl font-bold">{p.unit_type_name}</h3>
                  <div className="text-xs text-[#5E7166]">Effective {formatDate(p.effective_date)}</div>
                </div>
                {canEdit && <button onClick={() => setEditing(p)} className="btn-outline-emerald text-xs" data-testid={`edit-pricing-${p.unit_type_name.replace(/\s/g,'-')}`}>Update</button>}
              </div>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-[#5E7166]">Rent</div><div className="text-right font-semibold">{naira(p.rent_amount)}</div>
                <div className="text-[#5E7166]">Caution</div><div className="text-right">{naira(p.caution_fee)}</div>
                <div className="text-[#5E7166]">Agreement</div><div className="text-right">{naira(p.agreement_fee)}</div>
                <div className="text-[#5E7166]">Service</div><div className="text-right">{naira(p.service_charge)}</div>
                <div className="text-[#5E7166]">Commission ({p.commission_rate}%)</div><div className="text-right">{naira(p.commission_amount)}</div>
                <div className="text-[#5E7166] font-bold border-t border-[#E5EBE8] pt-2 mt-1">Total</div>
                <div className="text-right naira-total border-t border-[#E5EBE8] pt-2 mt-1">{naira(p.rent_amount + p.caution_fee + p.agreement_fee + p.service_charge + p.commission_amount)}</div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Type</th><th>Version</th><th>Effective</th><th>Rent</th><th>Status</th><th>Reason</th><th></th></tr></thead>
              <tbody>
                {history.map(p => (
                  <tr key={p.id}>
                    <td className="font-semibold">{p.unit_type_name}</td>
                    <td>v{p.version}</td>
                    <td>{formatDate(p.effective_date)}</td>
                    <td>{naira(p.rent_amount)}</td>
                    <td><span className={statusBadgeClass(p.active_status ? "active" : "archived")}>{p.active_status ? "Active" : "Archived"}</span></td>
                    <td className="text-xs">{p.change_reason || "—"}</td>
                    <td>{!p.active_status && canRollback && <button onClick={() => rollback(p.id)} className="btn-outline-emerald text-xs"><RotateCcw size={12}/> Roll back</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-pricing-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Update Pricing — {editing.unit_type_name}</h3>
              <button onClick={() => setEditing(null)}><X size={20}/></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input type="hidden" name="unit_type_name" value={editing.unit_type_name}/>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Rent</span><input name="rent" type="number" defaultValue={editing.rent_amount} required className="input-modav mt-1" data-testid="pricing-rent"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Caution</span><input name="caution" type="number" defaultValue={editing.caution_fee} required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Agreement Fee</span><input name="agreement" type="number" defaultValue={editing.agreement_fee} required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Service Charge</span><input name="service" type="number" defaultValue={editing.service_charge} required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Commission %</span><input name="commission_rate" type="number" step="0.1" defaultValue={editing.commission_rate} required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Discount</span><input name="discount" type="number" defaultValue={editing.discount_amount || 0} className="input-modav mt-1"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Promotion Note</span><input name="promo" defaultValue={editing.promotion_note || ""} className="input-modav mt-1"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Reason for change</span><textarea name="reason" required className="input-modav mt-1" rows={3} data-testid="pricing-reason"/></label>
              <div className="text-xs text-[#5E7166] bg-[#FDF6E3]/40 p-3 rounded-lg">Commission is recalculated as (Rent + Caution) × Rate%. Existing signed leases are NOT affected.</div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-gold flex-1 justify-center" data-testid="save-pricing-btn">Save New Version</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
